import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import emailjs from "@emailjs/browser";

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const FloatingButton = styled.button`
  position: fixed;
  bottom: 6rem;
  right: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${({ theme }) => theme.primary};
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  z-index: 1000;
  animation: ${floatAnimation} 3s ease-in-out infinite;

  &:hover {
    transform: scale(1.1);
    background: ${({ theme }) => theme.secondary};
  }

  @media (max-width: 768px) {
    bottom: 4rem;
    right: 1.5rem;
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
  ${({ isOpen }) =>
    isOpen &&
    `
    opacity: 1;
    pointer-events: all;
  `}
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.card.background};
  padding: 2rem;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  position: relative;
  animation: ${fadeIn} 0.3s ease;
  transform: translateY(${({ isOpen }) => (isOpen ? "0" : "20px")});
  transition: transform 0.3s ease;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${({ theme }) => theme.text.primary};
  transition: color 0.3s ease;

  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  padding: 0.8rem;
  border: 2px solid ${({ theme }) => theme.text.light};
  border-radius: 6px;
  background: ${({ theme }) =>
    theme.background === "#1a1a1a" ? "#2a2a2a" : theme.surface} !important;
  color: ${({ theme }) => theme.text.primary} !important;
  width: 100%;
  transition: all 0.3s ease;
  -webkit-text-fill-color: ${({ theme }) => theme.text.primary} !important;
  font-family: inherit !important;

  &::placeholder {
    color: ${({ theme }) => theme.text.secondary} !important;
    opacity: 0.7;
    -webkit-text-fill-color: ${({ theme }) => theme.text.secondary} !important;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
    background: ${({ theme }) =>
      theme.background === "#1a1a1a" ? "#3a3a3a" : "#ffffff"} !important;
    color: ${({ theme }) => theme.text.primary} !important;
    -webkit-text-fill-color: ${({ theme }) => theme.text.primary} !important;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px
      ${({ theme }) =>
        theme.background === "#1a1a1a" ? "#2a2a2a" : theme.surface}
      inset !important;
    -webkit-text-fill-color: ${({ theme }) => theme.text.primary} !important;
    color: ${({ theme }) => theme.text.primary} !important;
    transition: background-color 5000s ease-in-out 0s;
  }

  &:not(:placeholder-shown) {
    color: ${({ theme }) => theme.text.primary} !important;
    -webkit-text-fill-color: ${({ theme }) => theme.text.primary} !important;
  }
`;

const TextArea = styled.textarea`
  padding: 0.8rem;
  border: 2px solid ${({ theme }) => theme.text.light};
  border-radius: 6px;
  background: ${({ theme }) =>
    theme.background === "#1a1a1a" ? "#2a2a2a" : theme.surface} !important;
  color: ${({ theme }) => theme.text.primary} !important;
  min-height: 120px;
  width: 100%;
  resize: vertical;
  transition: all 0.3s ease;
  -webkit-text-fill-color: ${({ theme }) => theme.text.primary} !important;
  font-family: inherit !important;

  &::placeholder {
    color: ${({ theme }) => theme.text.secondary} !important;
    opacity: 0.7;
    -webkit-text-fill-color: ${({ theme }) => theme.text.secondary} !important;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
    background: ${({ theme }) =>
      theme.background === "#1a1a1a" ? "#3a3a3a" : "#ffffff"} !important;
    color: ${({ theme }) => theme.text.primary} !important;
    -webkit-text-fill-color: ${({ theme }) => theme.text.primary} !important;
  }

  &:not(:placeholder-shown) {
    color: ${({ theme }) => theme.text.primary} !important;
    -webkit-text-fill-color: ${({ theme }) => theme.text.primary} !important;
  }
`;

const SubmitButton = styled.button`
  padding: 0.8rem;
  background: ${({ theme }) => theme.primary};
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.secondary};
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: ${({ theme }) => theme.text.light};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// EmailJS Configuration - Using environment variables for security
const EMAILJS_CONFIG = {
  SERVICE_ID: process.env.REACT_APP_EMAILJS_SERVICE_ID,
  TEMPLATE_ID: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
  PUBLIC_KEY: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
};

const ContactButton = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("");

    const form = e.target;

    // Check if EmailJS is properly configured
    if (
      !EMAILJS_CONFIG.PUBLIC_KEY ||
      EMAILJS_CONFIG.PUBLIC_KEY.includes("xxxxxxx")
    ) {
      console.error(
        "EmailJS not configured! Please check environment variables or update EMAILJS_CONFIG."
      );
      setSubmitStatus("config-error");
      setIsSubmitting(false);
      return;
    }

    try {
      console.log("Sending email via EmailJS...");

      // Send email using EmailJS
      const response = await emailjs.sendForm(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        form,
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      console.log("Email sent successfully:", response);
      setSubmitStatus("success");
      form.reset();

      setTimeout(() => {
        setIsModalOpen(false);
        setSubmitStatus("");
      }, 2000);
    } catch (error) {
      console.error("Email sending failed:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <FloatingButton onClick={() => setIsModalOpen(true)}>
        <i className="fas fa-envelope"></i>
      </FloatingButton>

      <Modal isOpen={isModalOpen} onClick={() => setIsModalOpen(false)}>
        <ModalContent isOpen={isModalOpen} onClick={(e) => e.stopPropagation()}>
          <CloseButton onClick={() => setIsModalOpen(false)}>×</CloseButton>
          <h2 style={{ marginBottom: "1.5rem", color: "inherit" }}>
            Get in Touch
          </h2>

          <Form onSubmit={handleSubmit}>
            <Input
              type="text"
              name="from_name"
              placeholder="Your Name"
              required
            />
            <Input
              type="email"
              name="from_email"
              placeholder="Your Email"
              required
            />
            <TextArea name="message" placeholder="Your message..." required />

            {submitStatus === "success" && (
              <div
                style={{
                  color: "#28a745",
                  textAlign: "center",
                  marginBottom: "1rem",
                  padding: "0.5rem",
                  backgroundColor: "rgba(40, 167, 69, 0.1)",
                  borderRadius: "4px",
                }}
              >
                ✅ Message sent successfully! I'll get back to you soon.
              </div>
            )}

            {submitStatus === "config-error" && (
              <div
                style={{
                  color: "#ffc107",
                  textAlign: "center",
                  marginBottom: "1rem",
                  padding: "0.5rem",
                  backgroundColor: "rgba(255, 193, 7, 0.1)",
                  borderRadius: "4px",
                }}
              >
                ⚠️ Contact form not configured yet. Please check
                EMAILJS_SETUP.md for setup instructions.
              </div>
            )}

            {submitStatus === "error" && (
              <div
                style={{
                  color: "#dc3545",
                  textAlign: "center",
                  marginBottom: "1rem",
                  padding: "0.5rem",
                  backgroundColor: "rgba(220, 53, 69, 0.1)",
                  borderRadius: "4px",
                }}
              >
                ❌ Failed to send message. Please try again or contact me
                directly.
              </div>
            )}

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send Message"}
            </SubmitButton>
          </Form>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ContactButton;
